import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";

/**
 * Reports each page the app shows to Solstack's own visit log
 * (worker/analytics.js → POST /api/pv, read back on /admin/analytics).
 * First-party only: no cookies, no third-party script. Every page, prerendered
 * or not, mounts the same router, so one component in App.js sees the first
 * page of a load and every in-app navigation after it.
 *
 * Opening any page with ?notrack=1 stops reports from that browser (per host,
 * since each keeps its own localStorage); ?notrack=0 turns them back on.
 */
const OPT_OUT_KEY = "solstack:notrack";
// Only these query params are reported — the token/wallet a tool was opened
// on and campaign tags. Mirrors KEEP_PARAMS in worker/analytics.js.
const KEEP_PARAMS = ["mint", "address", "ref", "utm_source", "utm_medium", "utm_campaign"];
// A redirect (legacy slug → new slug) lands within a render or two; waiting
// this long reports only where the visitor ended up.
const SETTLE_MS = 300;

/** Whether this browser opted out with ?notrack=1. Reading it also applies a ?notrack= in the current URL. */
export function optedOut() {
  try {
    const flag = new URLSearchParams(window.location.search).get("notrack");
    if (flag === "1") localStorage.setItem(OPT_OUT_KEY, "1");
    if (flag === "0") localStorage.removeItem(OPT_OUT_KEY);
    return localStorage.getItem(OPT_OUT_KEY) === "1";
  } catch {
    return false;
  }
}

export function PageViews() {
  const { pathname, search } = useLocation();
  const landing = useRef(true);
  const lastPath = useRef("");

  useEffect(() => {
    const timer = setTimeout(() => {
      if (navigator.webdriver || optedOut()) return;
      const params = new URLSearchParams(search);
      const kept = new URLSearchParams();
      for (const k of KEEP_PARAMS) if (params.get(k)) kept.set(k, params.get(k));
      const q = kept.toString();
      const path = q ? `${pathname}?${q}` : pathname;
      if (path === lastPath.current) return;
      lastPath.current = path;
      const body = JSON.stringify({ p: path, r: landing.current ? document.referrer : "", l: landing.current ? 1 : 0 });
      landing.current = false;
      try {
        if (navigator.sendBeacon?.("/api/pv", new Blob([body], { type: "text/plain" }))) return;
      } catch {
        // fall through to fetch
      }
      fetch("/api/pv", { method: "POST", body, keepalive: true }).catch(() => {});
    }, SETTLE_MS);
    return () => clearTimeout(timer);
  }, [pathname, search]);

  return null;
}
